import { EraseIcon, SearchIcon } from "@theme/assets";
import { useTranslation } from "gatsby-plugin-react-i18next";
import {
  type ComponentPropsWithoutRef,
  type FC,
  type KeyboardEventHandler,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";

import {
  AutoCompleteContent,
  AutoCompleteInput,
  AutoCompleteList,
  AutoCompleteOption,
  AutoCompleteRoot,
  IconButton,
  Label,
  TextField,
} from "@theme/components";

interface SearchFieldProps extends Omit<ComponentPropsWithoutRef<"input">, "type"> {
  value: string;
  onValueChange: (next: string) => void;
  onKeyDown: () => void;
  onClear?: () => void;
  submitOnEnter?: boolean;
}

interface ClearButtonProps {
  action: () => void;
  controlsId?: string;
}

const ClearButton: FC<ClearButtonProps> = ({ action, controlsId }) => (
  <IconButton
    onClick={action}
    type="button"
    aria-label="Clear search input"
    aria-controls={controlsId}
    sx={{
      variant: "forms.trailingButton",
      WebkitTapHighlightColor: "transparent",
      touchAction: "manipulation",
      "@media (hover: none) and (pointer: coarse)": {
        "&:hover": {
          bg: "transparent",
        },
        "&:active": {
          bg: "transparent",
        },
      },
      svg: {
        color: "textPrimary",
      },
    }}
  >
    <EraseIcon width="24px" height="24px" />
  </IconButton>
);

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  ({ id = "search", value, onValueChange, onKeyDown, submitOnEnter = true, ...props }, ref) => {
    const { t } = useTranslation("search");

    const searchRef = useRef<HTMLInputElement>(null);
    const handleClear = () => {
      searchRef.current?.focus();
      onValueChange("");
    };

    useImperativeHandle(ref, () => searchRef.current as HTMLInputElement);

    const handleEnter: KeyboardEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        if (e.key === "Enter" && !e.nativeEvent.isComposing && submitOnEnter) {
          e.preventDefault();
          onKeyDown();
          searchRef.current?.blur();
        }
      },
      [onKeyDown, submitOnEnter],
    );

    return (
      <>
        <Label id={`${id}-label`} htmlFor="search" variant="screenReaderOnly">
          Search
        </Label>
        <TextField
          id={id}
          ref={searchRef}
          name="search"
          type="search"
          autoComplete="off"
          placeholder={t("searchFieldPlaceholder", { defaultValue: "Try \u201CShaving Kit\u201D" })}
          value={value}
          onChange={(e) => onValueChange(e.currentTarget.value)}
          onKeyDown={handleEnter}
          LeadingIcon={SearchIcon}
          TrailingIcon={
            value ? () => <ClearButton action={handleClear} controlsId={id} /> : undefined
          }
          showLabel={false}
          aria-labelledby={`${id}-label`}
          sx={{
            width: "100%",
            textAlign: "left",
          }}
          {...props}
        />
      </>
    );
  },
);
SearchField.displayName = "SearchField";

interface DyAutoSuggestSuggestion {
  term: string;
}

interface SearchFieldWithAutocompleteProps extends SearchFieldProps {
  suggestions: DyAutoSuggestSuggestion[];
  onSelectSuggestion: (s: DyAutoSuggestSuggestion["term"]) => void;
  closeOnBlur?: boolean;
}

export const SearchFieldWithAutocomplete: FC<SearchFieldWithAutocompleteProps> = ({
  suggestions,
  onSelectSuggestion,
  onKeyDown,
  value,
  onValueChange,
  id,
  ...props
}) => (
  <AutoCompleteRoot
    id={id}
    suggestionsLength={suggestions.length}
    syncKey={value}
    onSelectIndex={(i) => {
      const s = suggestions[i];
      if (s) onSelectSuggestion(s.term);
    }}
  >
    {({ notifyInteracted, clearActive }) => (
      <>
        <AutoCompleteInput>
          <SearchField
            id={id}
            value={value}
            onValueChange={(next) => {
              if (next) notifyInteracted();
              clearActive();
              onValueChange(next);
            }}
            onKeyDown={onKeyDown}
            {...props}
          />
        </AutoCompleteInput>
        <AutoCompleteContent>
          <AutoCompleteList
            sx={{
              width: "var(--radix-popper-anchor-width, 100%)",
              py: "s400",
              px: "s600",
              bg: "backgroundPrimary",
              borderRadius: "sm",
              boxShadow: "elevationMid",
            }}
          >
            {suggestions.map((s, index) => (
              <AutoCompleteOption
                key={s.term}
                index={index}
                label={s.term}
                query={value}
                onSelect={() => onSelectSuggestion(s.term)}
              />
            ))}
          </AutoCompleteList>
        </AutoCompleteContent>
      </>
    )}
  </AutoCompleteRoot>
);
SearchFieldWithAutocomplete.displayName = "SearchFieldWithAutocomplete";
